import React, { useState } from "react";
import slugify from "../utils/slugify";
import slugMap from "../data/image_slug_map.json";
const fallbackEmojis = {
  default: "❓",
  food_and_drink: "🍽️",
  animals: "🐾",
  clothing: "👕",
  furniture: "🪑",
  toys: "🧸",
  school: "📚",
  weather: "⛅",
  numbers: "🔢",
  colors: "🎨",
  core_words: "🔤",
  days_months: "📆"
};

const specialEmojiMap = {
  // Numbers
  eins: "1️⃣",
  zwei: "2️⃣",
  drei: "3️⃣",
  vier: "4️⃣",
  fünf: "5️⃣",
  sechs: "6️⃣",
  sieben: "7️⃣",
  acht: "8️⃣",
  neun: "9️⃣",
  null: "0️⃣",
  zehn: "1️⃣0️⃣",
  elf: "1️⃣1️⃣",
  zwölf: "1️⃣2️⃣",
  dreizehn: "1️⃣3️⃣",
  vierzehn: "1️⃣4️⃣",
  fünfzehn: "1️⃣5️⃣",
  sechzehn: "1️⃣6️⃣",
  siebzehn: "1️⃣7️⃣",
  achtzehn: "1️⃣8️⃣",
  neunzehn: "1️⃣9️⃣",
  zwanzig: "2️⃣0️⃣",
  dreißig: "3️⃣0️⃣",
  vierzig: "4️⃣0️⃣",
  fünfzig: "5️⃣0️⃣",
  sechzig: "6️⃣0️⃣",
  siebzig: "7️⃣0️⃣",
  achtzig: "8️⃣0️⃣",
  neunzig: "9️⃣0️⃣",
  hundert: "1️⃣0️⃣0️⃣",
  tausend: "1️⃣0️⃣0️⃣0️⃣",

  // Core words
  ja: "✅",
  nein: "❌",
  und: "➕",
  oder: "↔",
  ich: "🧒",
  du: "🫵",
  danke: "🙏",
  bitte: "🙇",
  was: "❓",
  warum: "🤔",
  wer: "🧠",
  wie: "🗣️",
  wo: "📍",
  wann: "⏰",
  der: "🧍",
  die: "👧",
  das: "🎁",
  ein: "1️⃣",
  eine: "🧺",
  nicht: "🚫",
  kein: "❌",
  weil: "🧠",
  dass: "🔗",
  es: "⚪",
  wir: "👨‍👩‍👧‍👦",
  ihr: "👨‍👩‍👧‍👦",
  mein: "👉",
  dein: "🫵",
  sein: "👦",
  unser: "🤝",
  euer: "🙋‍♂️🙋‍♀️"
};

// Helper to generate slug variants
function slugifyVariants(str) {
  // Current frontend logic
  const current = slugify(str);
  // Fallback: ASCII for umlauts, no underscores for them, no hyphens/spaces
  const fallback = str
    .toLowerCase()
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ß/g, "ss")
    .replace(/-/g, "")
    .replace(/ /g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .replace(/^_+|_+$/g, "");
  // Return both, unique
  return [...new Set([current, fallback])];
}

export default function WordImage({ word, language, category, maxWidthPct }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  if (!word || typeof word !== "string") return null;
  // Manual override for image filenames with irregular slugified results
  const imageOverrides = {
    ärztin: "rztin"
  };
  const slugVariants = imageOverrides[word.toLowerCase()]
    ? [imageOverrides[word.toLowerCase()]]
    : slugMap[language]?.[word]
    ? [slugMap[language][word]]
    : slugifyVariants(word);

  const imgPath = `/images/words/${language}/${slugVariants[imgIndex]}.webp`;

  function handleImgError() {
    if (imgIndex < slugVariants.length - 1) {
      setImgIndex(imgIndex + 1);
    } else {
      setImgFailed(true);
    }
  }

  if (imgFailed) {
    const emoji = specialEmojiMap[word.toLowerCase()] || fallbackEmojis[category] || "❓";
    return (
      <div style={{ fontSize: "3rem", margin: "10px" }} title={word}>
        {emoji}
      </div>
    );
  }

  // Responsive image sizing
  let style = { margin: "10px" };
  if (typeof maxWidthPct === "number") {
    style.maxWidth = `${maxWidthPct}vw`;
    style.maxHeight = `${maxWidthPct}vw`;
  } else {
    style.maxHeight = "180px";
  }

  return (
    <img
      src={imgPath}
      alt={word}
      onError={handleImgError}
      style={style}
    />
  );
}